import { skipToken } from '@reduxjs/toolkit/query';

import { canRenderNativeJoin } from './appointmentActionSession';

import type { Session } from '@/types';

import { useGetMomentsParticipantAppointmentQuery } from '@/store/api/momentsParticipantAppointmentApi';

// MEN-5454: the link a Rails Session's Join button opens.
//
// Rails withholds `location` for a session the calendar migration rebound,
// because its stored Cal-era Meet URL is not the room on the Google event
// Moments created. For such a session (no location, but a Moments appointment)
// the link comes from the appointment and is shown only when Moments allows
// join. Loading, a failed read or a denied join all yield no link: a missing
// button is acceptable, a wrong room is not.
//
// `enabled` is the caller's own Join gate (not done or cancelled, happening
// today) so only sessions that could show Join read Moments.
export const useSessionJoinUrl = (
  session: Pick<Session, 'location' | 'sourceAppointmentId'> | undefined,
  { enabled }: { enabled: boolean },
): string | undefined => {
  const appointmentId =
    !session?.location && session?.sourceAppointmentId
      ? session.sourceAppointmentId
      : undefined;
  // currentData, not data: RTK Query keeps the previous argument's result in
  // `data` while the next one loads, which would open another session's room.
  const { currentData, isError } = useGetMomentsParticipantAppointmentQuery(
    enabled && appointmentId ? { appointmentId } : skipToken,
  );

  if (session?.location) return session.location;
  if (!appointmentId || !enabled || isError) return undefined;
  const data = currentData?.id === appointmentId ? currentData : undefined;
  if (!data) return undefined;
  return canRenderNativeJoin(data)
    ? (data.meeting.joinUrl ?? undefined)
    : undefined;
};
